import { useState, useEffect, useCallback, useRef } from 'react';
import { Store, Loader2, Copy, Check, Search, ChevronLeft, ChevronRight, X, Package, Lock, Eye, Database, ShoppingCart } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/* ── Hover Preview Tooltip ── */
function CastPreviewTooltip({ cast, position }) {
  const bodyText = cast.body || cast.cast || '';
  return (
    <div
      className="fixed z-[60] w-[400px] max-h-[320px] bg-[#0C1A30]/95 backdrop-blur-xl border border-[#8B5CF6]/30 rounded-xl shadow-2xl shadow-purple-900/30 overflow-hidden pointer-events-none"
      style={{ top: position.top, left: position.left, animation: 'fadeIn 0.15s ease-out' }}
      data-testid="cast-preview-tooltip"
    >
      <div className="p-3 border-b border-white/[0.06]">
        <h4 className="text-white font-bold text-xs leading-snug line-clamp-2">{cast.title}</h4>
        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
          <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-[#8B5CF6]/15 text-[#8B5CF6]">{cast.platform_label || cast.platform || cast.target_ai}</span>
          <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-[#06B6D4]/15 text-[#06B6D4]">{cast.domain_label || cast.domain || cast.category}</span>
          {cast.role_label && <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400">{cast.role_label}</span>}
        </div>
      </div>
      <div className="p-3 overflow-y-auto max-h-[200px]">
        <pre className="text-white/70 text-[11px] leading-relaxed whitespace-pre-wrap font-mono">{bodyText.slice(0, 500)}{bodyText.length > 500 ? '...' : ''}</pre>
      </div>
    </div>
  );
}

/* ── Full Expand Modal ── */
function CastExpandView({ cast, onClose }) {
  const [copied, setCopied] = useState(false);
  const bodyText = cast.body || cast.cast || '';
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0A1628] border border-[#8B5CF6]/20 rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-testid="cast-expand-view">
        <div className="flex items-center justify-between p-5 border-b border-white/[0.06]">
          <div>
            <h3 className="text-white font-bold text-sm">{cast.title}</h3>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className="text-[10px] capitalize px-2 py-0.5 rounded-full bg-[#8B5CF6]/10 text-[#8B5CF6] font-bold">{cast.platform_label || cast.platform || cast.target_ai}</span>
              <span className="text-[10px] capitalize px-2 py-0.5 rounded-full bg-[#06B6D4]/10 text-[#06B6D4] font-bold">{cast.domain_label || cast.domain || cast.category}</span>
              {cast.role_label && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 font-bold">{cast.role_label}</span>}
              {cast.specific_label && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 font-bold">{cast.specific_label}</span>}
            </div>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white" data-testid="close-expand"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto max-h-[60vh]">
          <pre className="text-white/80 text-sm leading-relaxed whitespace-pre-wrap font-mono">{bodyText}</pre>
        </div>
        <div className="flex items-center gap-3 p-5 border-t border-white/[0.06]">
          <Button onClick={() => { navigator.clipboard.writeText(bodyText); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
            className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white rounded-full px-6 text-sm font-bold" data-testid="copy-cast-btn">
            {copied ? <><Check className="w-4 h-4 mr-2" /> Copied!</> : <><Copy className="w-4 h-4 mr-2" /> Copy Cast</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ── Cast Card with Hover Preview ── */
function CastCard({ cast, onExpand, onCopy, copiedId }) {
  const [hovered, setHovered] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const cardRef = useRef(null);
  const timerRef = useRef(null);
  const bodyText = cast.body || cast.cast || '';

  const handleMouseEnter = () => {
    timerRef.current = setTimeout(() => {
      if (cardRef.current) {
        const rect = cardRef.current.getBoundingClientRect();
        const spaceRight = window.innerWidth - rect.right;
        const left = spaceRight > 420 ? rect.right + 8 : rect.left - 408;
        const top = Math.min(rect.top, window.innerHeight - 340);
        setTooltipPos({ top: Math.max(8, top), left: Math.max(8, left) });
        setHovered(true);
      }
    }, 350);
  };
  const handleMouseLeave = () => { clearTimeout(timerRef.current); setHovered(false); };

  return (
    <>
      <div
        ref={cardRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="bg-[#0E1830] border border-white/[0.08] rounded-xl p-4 hover:border-[#8B5CF6]/30 hover:shadow-lg hover:shadow-purple-900/10 transition-all duration-200 group cursor-pointer"
        data-testid={`cast-card-${cast.id}`}
      >
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="text-white font-semibold text-xs leading-tight line-clamp-2 group-hover:text-[#c4b5fd] transition-colors">{cast.title}</h3>
        </div>
        <p className="text-white/35 text-[11px] line-clamp-2 mb-3 leading-relaxed">{bodyText.slice(0, 120)}</p>
        <div className="flex items-center gap-1.5 text-[9px] text-white/30 mb-3 flex-wrap">
          <span className="capitalize px-1.5 py-0.5 rounded-full bg-[#8B5CF6]/10 text-[#8B5CF6] font-bold">{cast.platform_label || cast.platform || cast.target_ai}</span>
          <span className="capitalize px-1.5 py-0.5 rounded-full bg-[#06B6D4]/10 text-[#06B6D4] font-bold">{cast.domain_label || cast.domain || cast.category}</span>
          {cast.role_label && <span className="capitalize px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-400 font-bold">{cast.role_label}</span>}
        </div>
        <div className="flex items-center gap-2 pt-3 border-t border-white/[0.06]">
          <button onClick={(e) => { e.stopPropagation(); onExpand(cast); }}
            className="flex items-center gap-1 px-3 py-1.5 text-[11px] font-bold rounded-lg bg-white/[0.06] text-white/60 hover:text-white hover:bg-white/[0.1] border border-white/[0.08] transition-all"
            data-testid={`view-cast-${cast.id}`}>
            <Eye className="w-3 h-3" /> View
          </button>
          <button onClick={(e) => { e.stopPropagation(); onCopy(cast); }}
            className="flex items-center gap-1 px-3 py-1.5 text-[11px] font-bold rounded-lg bg-white/[0.06] text-white/60 hover:text-white border border-white/[0.08] transition-all"
            data-testid={`copy-cast-${cast.id}`}>
            {copiedId === cast.id ? <><Check className="w-3 h-3 text-emerald-400" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
          </button>
        </div>
      </div>
      {hovered && <CastPreviewTooltip cast={cast} position={tooltipPos} />}
    </>
  );
}

/* ── Package Card ── */
function PackageCard({ pkg, onAddToLibrary, adding }) {
  const isDomain = pkg.source === 'domain_seed';
  const castCount = pkg.cast_count || 0;
  return (
    <div className="bg-[#0E1830] border border-white/[0.08] rounded-xl p-4 hover:border-[#8B5CF6]/20 transition-all group" data-testid={`package-${pkg.id}`}>
      <div className="flex items-start justify-between mb-2">
        <h3 className="text-white font-bold text-sm group-hover:text-[#c4b5fd] transition-colors">{pkg.name}</h3>
        <span className="text-[10px] font-bold rounded-full px-2.5 py-0.5 whitespace-nowrap bg-emerald-500/15 text-emerald-400 border border-emerald-500/20">FREE</span>
      </div>
      <p className="text-white/35 text-[11px] mt-1 leading-relaxed line-clamp-2 mb-3">{pkg.description}</p>
      <div className="flex items-center gap-2 text-[9px] text-white/30 mb-3 flex-wrap">
        <span className="capitalize px-1.5 py-0.5 rounded-full bg-[#8B5CF6]/10 text-[#8B5CF6] font-bold">{pkg.category}</span>
        <span className="text-white/25">{castCount.toLocaleString()} casts</span>
        {isDomain && <span className="px-1.5 py-0.5 rounded-full bg-[#06B6D4]/10 text-[#06B6D4] font-bold">6 platforms</span>}
      </div>
      <Button onClick={() => onAddToLibrary(pkg.id)} disabled={adding === pkg.id}
        className="w-full bg-[#8B5CF6]/15 text-[#8B5CF6] hover:bg-[#8B5CF6]/25 border border-[#8B5CF6]/20 rounded-lg text-xs font-bold h-9"
        data-testid={`add-package-${pkg.id}`}>
        {adding === pkg.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><ShoppingCart className="w-3 h-3 mr-1.5" /> Add to Library</>}
      </Button>
    </div>
  );
}

/* ── Domain filter chips ── */
const TOP_DOMAINS = [
  '', 'software-engineering', 'web-development', 'marketing', 'sales', 'finance',
  'design', 'ui-ux', 'data-science', 'machine-learning', 'legal', 'healthcare',
];
const DOMAIN_LABELS = {
  '': 'All Domains', 'software-engineering': 'Software Eng', 'web-development': 'Web Dev',
  'marketing': 'Marketing', 'sales': 'Sales', 'finance': 'Finance',
  'design': 'Design', 'ui-ux': 'UI/UX', 'data-science': 'Data Science',
  'machine-learning': 'ML/AI', 'legal': 'Legal', 'healthcare': 'Healthcare',
};

const PLATFORMS = ['', 'chatgpt', 'claude', 'gemini', 'perplexity', 'copilot', 'cursor', 'emergent'];
const TASKS_FILTER = ['', 'write', 'analyze', 'brainstorm', 'debug', 'plan', 'research', 'review', 'explain', 'design', 'test'];

export default function PIStorePage() {
  // Default to 'casts' view — the 4.3M seeded casts are the primary content
  const [view, setView] = useState('casts');
  const [packages, setPackages] = useState([]);
  const [casts, setCasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [domain, setDomain] = useState('');
  const [platform, setPlatform] = useState('');
  const [task, setTask] = useState('');
  const [pkgCategory, setPkgCategory] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [addingPkg, setAddingPkg] = useState(null);
  const [expandedCast, setExpandedCast] = useState(null);
  const [dimensions, setDimensions] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/ai/casts/dimensions`).then(r => r.json()).then(setDimensions).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPackages = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (pkgCategory) params.append('category', pkgCategory);
      const res = await fetch(`${API}/api/store/packages?${params}`, { credentials: 'include' });
      if (res.ok) { const d = await res.json(); setPackages(d.packages || []); }
    } catch (err) { toast.error('Failed to load packages'); }
    finally { setLoading(false); }
  }, [pkgCategory]);

  const fetchSeededCasts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (domain) params.append('domain', domain);
      if (platform) params.append('platform', platform);
      if (task) params.append('task', task);
      if (search) params.append('search', search);
      params.append('page', String(page));
      params.append('limit', '24');
      const res = await fetch(`${API}/api/ai/casts?${params}`);
      if (res.ok) { const d = await res.json(); setCasts(d.casts || []); setTotal(d.total || 0); }
    } catch (err) { toast.error('Failed to load casts'); }
    finally { setLoading(false); }
  }, [domain, platform, task, search, page]);

  useEffect(() => {
    if (view === 'packages') fetchPackages();
    else fetchSeededCasts();
  }, [view, fetchPackages, fetchSeededCasts]);

  const handleAddPackage = async (pkgId) => {
    setAddingPkg(pkgId);
    try {
      const res = await fetch(`${API}/api/store/packages/${pkgId}/add-to-library`, { method: 'POST', credentials: 'include' });
      if (res.ok) { const d = await res.json(); toast.success(`Added ${d.casts_copied} casts to your library!`); }
      else { const err = await res.json().catch(() => ({})); toast.error(err.detail || 'Failed'); }
    } catch (err) { toast.error('Failed'); }
    finally { setAddingPkg(null); }
  };

  const handleCopy = (cast) => {
    const text = cast.body || cast.cast || '';
    navigator.clipboard.writeText(text);
    setCopiedId(cast.id);
    toast.success('Cast copied to clipboard');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleSearch = (e) => { e.preventDefault(); setSearch(searchInput); setPage(1); if (view === 'packages') setView('casts'); };
  const totalPages = Math.ceil(total / 24);

  return (
    <div className="space-y-5 max-w-6xl mx-auto" data-testid="store-page">
      {/* Header */}
      <div>
        <h1 className="font-space text-2xl md:text-3xl text-white mb-1">PI Store</h1>
        <p className="text-white/50 text-sm">
          {dimensions ? <><span className="text-[#8B5CF6] font-bold">{(dimensions.total_casts || 0).toLocaleString()}</span> precision casts across {dimensions.dimensions?.domains || 75} domains</> : 'Loading cast library...'}
          {' '}&middot; {dimensions?.dimensions?.platforms || 6} platforms &middot; {dimensions?.dimensions?.tasks || 30} task types
        </p>
      </div>

      {/* Tab bar + Search */}
      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="flex bg-white/[0.04] rounded-lg border border-white/[0.08] p-0.5 shrink-0">
          <button onClick={() => { setView('casts'); setPage(1); }}
            className={`flex items-center gap-1.5 px-4 py-1.5 text-xs font-bold rounded-md transition-all ${view === 'casts' ? 'bg-[#8B5CF6] text-white shadow-lg shadow-purple-900/30' : 'text-white/40 hover:text-white/60'}`}
            data-testid="tab-casts">
            <Database className="w-3 h-3" /> All Casts
          </button>
          <button onClick={() => { setView('packages'); setPage(1); }}
            className={`flex items-center gap-1.5 px-4 py-1.5 text-xs font-bold rounded-md transition-all ${view === 'packages' ? 'bg-[#8B5CF6] text-white shadow-lg shadow-purple-900/30' : 'text-white/40 hover:text-white/60'}`}
            data-testid="tab-packages">
            <Package className="w-3 h-3" /> Packages
          </button>
        </div>
        <form onSubmit={handleSearch} className="flex gap-2 flex-1 min-w-0">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <input value={searchInput} onChange={e => setSearchInput(e.target.value)}
              placeholder="Search 4.3M casts by title, domain, role..."
              className="w-full pl-9 pr-3 py-2 bg-white/[0.06] border border-white/[0.1] rounded-lg text-white text-sm focus:border-[#8B5CF6]/40 focus:outline-none placeholder:text-white/30"
              data-testid="store-search" />
          </div>
          <Button type="submit" className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white rounded-lg px-4 font-bold text-xs shrink-0">Search</Button>
        </form>
      </div>

      {/* Filters — shown for casts view */}
      {view === 'casts' && (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1.5">
            <span className="text-white/30 text-[10px] uppercase tracking-wider self-center mr-1 font-bold">Domain:</span>
            {TOP_DOMAINS.map(d => (
              <button key={d || 'all'} onClick={() => { setDomain(d); setPage(1); }}
                className={`px-2.5 py-1 text-[11px] rounded-full border font-semibold transition-all ${domain === d ? 'bg-[#8B5CF6] text-white border-[#8B5CF6]' : 'bg-white/[0.04] text-white/50 border-white/[0.1] hover:text-white'}`}
                data-testid={`domain-filter-${d || 'all'}`}>
                {DOMAIN_LABELS[d] || d}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-1.5">
            <span className="text-white/30 text-[10px] uppercase tracking-wider self-center mr-1 font-bold">Platform:</span>
            {PLATFORMS.map(p => (
              <button key={p || 'all'} onClick={() => { setPlatform(p); setPage(1); }}
                className={`px-2.5 py-1 text-[11px] rounded-full border font-semibold capitalize transition-all ${platform === p ? 'bg-[#06B6D4] text-white border-[#06B6D4]' : 'bg-white/[0.04] text-white/50 border-white/[0.1] hover:text-white'}`}
                data-testid={`platform-filter-${p || 'all'}`}>
                {p || 'All'}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-1.5">
            <span className="text-white/30 text-[10px] uppercase tracking-wider self-center mr-1 font-bold">Task:</span>
            {TASKS_FILTER.map(t => (
              <button key={t || 'all'} onClick={() => { setTask(t); setPage(1); }}
                className={`px-2.5 py-1 text-[11px] rounded-full border font-semibold capitalize transition-all ${task === t ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white/[0.04] text-white/50 border-white/[0.1] hover:text-white'}`}
                data-testid={`task-filter-${t || 'all'}`}>
                {t || 'All Tasks'}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-[#8B5CF6] animate-spin" /></div>
      ) : view === 'packages' ? (
        <>
          <div className="flex flex-wrap gap-1.5">
            <span className="text-white/30 text-[10px] uppercase tracking-wider self-center mr-1 font-bold">Filter:</span>
            {['', 'coding', 'marketing', 'sales', 'design', 'writing', 'research', 'video', 'general'].map(c => (
              <button key={c || 'all'} onClick={() => setPkgCategory(c)}
                className={`px-2.5 py-1 text-[11px] rounded-full border font-semibold capitalize transition-all ${pkgCategory === c ? 'bg-[#8B5CF6] text-white border-[#8B5CF6]' : 'bg-white/[0.04] text-white/50 border-white/[0.1] hover:text-white'}`}
                data-testid={`pkg-cat-${c || 'all'}`}>
                {c || 'All'}
              </button>
            ))}
          </div>
          <p className="text-white/40 text-xs">{packages.length} packages available &middot; Each domain contains 57,600 casts across 6 platforms</p>
          {packages.length === 0 ? (
            <div className="flex flex-col items-center justify-center text-center py-16 bg-[#0E1830] border border-white/[0.08] rounded-2xl">
              <Package className="w-12 h-12 text-white/15 mb-4" />
              <p className="text-white/40 text-sm">No packages in this category</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {packages.map(pkg => <PackageCard key={pkg.id} pkg={pkg} onAddToLibrary={handleAddPackage} adding={addingPkg} />)}
            </div>
          )}
        </>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <p className="text-white/40 text-xs">
              {total.toLocaleString()} casts {search && <span className="text-[#8B5CF6]">matching &ldquo;{search}&rdquo;</span>}
              {(domain || platform || task) && <span className="text-white/25"> (filtered)</span>}
            </p>
            <p className="text-white/25 text-[10px] hidden sm:block">Hover over cards to preview</p>
          </div>
          {casts.length === 0 ? (
            <div className="flex flex-col items-center justify-center text-center py-16 bg-[#0E1830] border border-white/[0.08] rounded-2xl">
              <Store className="w-12 h-12 text-white/15 mb-4" />
              <p className="text-white/40 text-sm">No casts found for this filter</p>
              <p className="text-white/25 text-xs mt-1">Try a different combination of domain, platform, or task</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="casts-grid">
              {casts.map(cast => (
                <CastCard
                  key={cast.id}
                  cast={cast}
                  onExpand={setExpandedCast}
                  onCopy={handleCopy}
                  copiedId={copiedId}
                />
              ))}
            </div>
          )}
          {totalPages > 1 && (
            <div className="flex justify-center gap-3 pt-4">
              <Button variant="outline" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                className="border-white/15 text-white/50 text-xs rounded-full px-5" data-testid="prev-page">
                <ChevronLeft className="w-3 h-3 mr-1" /> Prev
              </Button>
              <span className="text-white/30 text-xs self-center" data-testid="page-indicator">
                Page {page} of {totalPages.toLocaleString()}
              </span>
              <Button variant="outline" onClick={() => setPage(p => p + 1)} disabled={casts.length < 24}
                className="border-white/15 text-white/50 text-xs rounded-full px-5" data-testid="next-page">
                Next <ChevronRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          )}
        </>
      )}

      {expandedCast && <CastExpandView cast={expandedCast} onClose={() => setExpandedCast(null)} />}

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
      `}</style>
    </div>
  );
}
